import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, X, Search, CheckCircle2, AlertCircle, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";

const EMPTY = {
  identification_type: "cedula", identification: "", name: "",
  email: "", phone: "", address: "", city: "", notes: "", active: true,
  tipo_contribuyente: null, obligado_contabilidad: null,
  contribuyente_especial: null, agente_retencion: null,
  actividad_economica: null, regimen: null, estado_sri: null,
  sri_synced_at: null,
};

const TYPE_LABELS = {
  cedula: "Cédula", ruc: "RUC", pasaporte: "Pasaporte", consumidor_final: "Consumidor Final",
};

export default function Customers() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/customers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/customers/${editing}`, form);
      else await api.post("/customers", form);
      toast.success(editing ? "Cliente actualizado" : "Cliente creado");
      setForm(EMPTY); setEditing(null); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditing(c.id); setShowForm(true); };
  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar cliente?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="customers-page">
      <PageHeader
        title="Clientes"
        subtitle="Base de clientes para facturación electrónica. Cédula o RUC se autocompleta con datos del SRI."
        testid="customers-header"
        action={
          <button data-testid="customers-create-button"
            onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nuevo cliente
          </button>
        }
      />

      {showForm && (
        <div data-testid="customer-form" className="card-soft p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold">{editing ? "Editar" : "Nuevo"} cliente</h3>
            <button onClick={() => setShowForm(false)} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Tipo identificación *</span>
              <select data-testid="customer-id-type" className="input-soft" value={form.identification_type}
                onChange={(e) => setForm({
                  ...form, identification_type: e.target.value, identification: "",
                  tipo_contribuyente: null, obligado_contabilidad: null,
                  contribuyente_especial: null, agente_retencion: null,
                  actividad_economica: null, regimen: null, estado_sri: null, sri_synced_at: null,
                })}>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <IdentificationField
              type={form.identification_type}
              value={form.identification}
              onChange={(val) => setForm((f) => ({ ...f, identification: val }))}
              onFound={(data) => setForm((f) => ({
                ...f,
                name: data.name || f.name,
                address: data.address || f.address,
                phone: data.phone || f.phone,
                email: data.email || f.email,
                ...sriExtraToFields(data),
              }))}
            />
            {form.estado_sri && (
              <div className="md:col-span-2">
                <SriBadges customer={form} />
              </div>
            )}
            <Field label="Nombre / Razón social *">
              <input data-testid="customer-name" required className="input-soft" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Email">
              <input type="email" className="input-soft" value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Teléfono">
              <input className="input-soft" value={form.phone || ""}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="Ciudad">
              <input className="input-soft" value={form.city || ""}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Field>
            <div className="md:col-span-2">
              <Field label="Dirección">
                <input className="input-soft" value={form.address || ""}
                  onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </Field>
            </div>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button data-testid="customer-save-button" className="btn-primary" type="submit">Guardar</button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="Sin clientes aún" hint="Crea tu primer cliente para facturar." testid="customers-empty" />
      ) : (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
                <th className="px-5 py-3">Tipo</th><th className="px-5 py-3">Identificación</th>
                <th className="px-5 py-3">Nombre</th><th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Teléfono</th><th className="px-5 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                  <td className="px-5 py-4 text-xs uppercase tracking-wider text-[#5C6B64]">{TYPE_LABELS[c.identification_type] || c.identification_type}</td>
                  <td className="px-5 py-4 font-mono">{c.identification}</td>
                  <td className="px-5 py-4 font-medium">
                    <div>{c.name}</div>
                    {c.estado_sri && (
                      <div className="mt-1.5"><SriBadges customer={c} /></div>
                    )}
                  </td>
                  <td className="px-5 py-4">{c.email || "—"}</td>
                  <td className="px-5 py-4">{c.phone || "—"}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => onEdit(c)} className="p-2 rounded hover:bg-[#F3F2ED]"><Pencil className="w-4 h-4 text-[#5C6B64]" /></button>
                      <button onClick={() => onDelete(c.id)} className="p-2 rounded hover:bg-[#F3DADA]"><Trash2 className="w-4 h-4 text-[#A44A4A]" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* Reusable identification field with auto-lookup */
export function IdentificationField({ type, value, onChange, onFound, label = "Identificación *", testid = "customer-identification" }) {
  const [status, setStatus] = useState("idle"); // idle | loading | found | sri | local | not_found | error
  const [meta, setMeta] = useState(null);
  const lastQueriedRef = useRef("");
  const debounceRef = useRef(null);

  const targetLen = type === "ruc" ? 13 : type === "cedula" ? 10 : null;

  const doLookup = useCallback(async (val) => {
    if (!val || lastQueriedRef.current === val) return;
    lastQueriedRef.current = val;
    setStatus("loading");
    setMeta(null);
    try {
      const r = await api.get(`/lookup/identification/${encodeURIComponent(val)}`);
      const d = r.data;
      if (d.found) {
        setStatus(d.source === "local" ? "local" : "sri");
        setMeta(d);
        onFound?.(d);
      } else {
        setStatus("not_found");
      }
    } catch {
      setStatus("error");
    }
  }, [onFound]);

  const handleChange = (raw) => {
    const onlyDigits = type === "pasaporte" ? raw : raw.replace(/\D/g, "");
    onChange(onlyDigits);
    setStatus("idle");
    setMeta(null);

    if (targetLen && onlyDigits.length === targetLen) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => doLookup(onlyDigits), 250);
    }
  };

  const statusBadge = () => {
    if (status === "loading") return <span className="flex items-center gap-1 text-[#8A9A92]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Consultando SRI…</span>;
    if (status === "local") return <span className="flex items-center gap-1 text-[#507A59]" data-testid="lookup-found-local"><CheckCircle2 className="w-3.5 h-3.5" /> Ya registrado: {meta?.name}</span>;
    if (status === "sri") return <span className="flex items-center gap-1 text-[#507A59]" data-testid="lookup-found-sri"><CheckCircle2 className="w-3.5 h-3.5" /> Encontrado en SRI{meta?.extra?.status && meta.extra.status !== "ACTIVO" ? ` (${meta.extra.status})` : ""}</span>;
    if (status === "not_found") return <span className="flex items-center gap-1 text-[#8C5A2B]" data-testid="lookup-not-found"><AlertCircle className="w-3.5 h-3.5" /> Sin datos en SRI. Complete manualmente.</span>;
    if (status === "error") return <span className="flex items-center gap-1 text-[#A44A4A]"><AlertCircle className="w-3.5 h-3.5" /> Error al consultar. Intente nuevamente.</span>;
    return null;
  };

  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      <div className="relative">
        <input
          data-testid={testid} required className="input-soft pr-10" value={value}
          maxLength={targetLen || undefined}
          inputMode={targetLen ? "numeric" : undefined}
          onChange={(e) => handleChange(e.target.value)}
        />
        <button type="button" onClick={() => value && doLookup(value)}
          aria-label="Buscar"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[#F3F2ED] text-[#5C6B64]"
          data-testid="lookup-search-button">
          {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </button>
      </div>
      <div className="text-[11px] mt-1.5 min-h-[16px]">{statusBadge()}</div>
    </label>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

/* Map SRI 'extra' fields → customer model fields (works for both sri and local sources) */
export function sriExtraToFields(data) {
  if (!data || !data.extra) return {};
  const ex = data.extra;
  if (data.source === "local") {
    // local DB returns final field names directly
    return {
      tipo_contribuyente: ex.tipo_contribuyente || null,
      obligado_contabilidad: ex.obligado_contabilidad || null,
      contribuyente_especial: ex.contribuyente_especial || null,
      agente_retencion: ex.agente_retencion || null,
      actividad_economica: ex.actividad_economica || null,
      regimen: ex.regimen || null,
      estado_sri: ex.estado_sri || null,
      sri_synced_at: ex.sri_synced_at || null,
    };
  }
  // sri source: extra uses SRI nomenclature
  return {
    tipo_contribuyente: ex.tipo_contribuyente || null,
    obligado_contabilidad: ex.obligado_contabilidad || null,
    contribuyente_especial: ex.contribuyente_especial || null,
    agente_retencion: ex.agente_retencion || null,
    actividad_economica: ex.actividad_economica || null,
    regimen: null,
    estado_sri: ex.status || null,
    sri_synced_at: new Date().toISOString(),
  };
}

/* Visual badges for SRI-sourced metadata */
export function SriBadges({ customer }) {
  if (!customer || !customer.estado_sri) return null;
  const items = [];
  if (customer.estado_sri && customer.estado_sri !== "ACTIVO")
    items.push({ label: customer.estado_sri, color: "bg-[#F3DADA] text-[#A44A4A]" });
  else if (customer.estado_sri)
    items.push({ label: "Activo SRI", color: "bg-[#E4EFE6] text-[#507A59]" });
  if (customer.tipo_contribuyente)
    items.push({ label: customer.tipo_contribuyente, color: "bg-[#F3F2ED] text-[#5C6B64]" });
  if (customer.obligado_contabilidad === "SI")
    items.push({ label: "Lleva contabilidad", color: "bg-[#DDE6ED] text-[#3B5A7A]" });
  if (customer.contribuyente_especial)
    items.push({ label: `Especial #${customer.contribuyente_especial}`, color: "bg-[#E4D5C7] text-[#2A3F35]" });
  if (customer.agente_retencion)
    items.push({ label: "Agente de retención", color: "bg-[#F3DEE5] text-[#A4486B]" });
  if (customer.actividad_economica)
    items.push({ label: customer.actividad_economica, color: "bg-[#F3F2ED] text-[#5C6B64] max-w-xs truncate" });
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="sri-badges">
      {items.map((it) => (
        <span key={it.label} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${it.color}`}>{it.label}</span>
      ))}
    </div>
  );
}


/* SRI status banner shown above invoice/POS submit
 * Returns:
 *   - null when customer is OK
 *   - yellow warning when estado_sri is not ACTIVO
 *   - amber info when status unknown / not synced
 */
export function SriStatusBanner({ customer, onRevalidated }) {
  const [revalidating, setRevalidating] = useState(false);
  if (!customer) return null;
  if (customer.identification_type === "consumidor_final") return null;

  const status = (customer.estado_sri || "").toUpperCase();
  const isOk = status === "ACTIVO";
  const isUnknown = !status;
  if (isOk) return null;

  const revalidate = async () => {
    if (!customer.identification) return;
    setRevalidating(true);
    try {
      const { data } = await api.get(`/lookup/identification/${encodeURIComponent(customer.identification)}?force_sri=true`);
      if (data.found && data.source === "sri") {
        const patch = {
          name: data.name || customer.name,
          address: data.address || customer.address,
          ...sriExtraToFields(data),
        };
        const updated = { ...customer, ...patch };
        await api.put(`/customers/${customer.id}`, updated);
        toast.success("Cliente revalidado en SRI");
        onRevalidated?.(updated);
      } else {
        toast.error("Sin datos vigentes en SRI para esta identificación");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al revalidar");
    } finally { setRevalidating(false); }
  };

  const tone = isUnknown
    ? { bg: "bg-[#FFF4E5]", border: "border-[#E0B377]", text: "text-[#8C5A2B]", iconColor: "text-[#8C5A2B]" }
    : { bg: "bg-[#FDE7E7]", border: "border-[#E08B8B]", text: "text-[#8C2B2B]", iconColor: "text-[#A44A4A]" };

  return (
    <div
      data-testid="sri-status-banner"
      data-status={isUnknown ? "unknown" : status.toLowerCase()}
      className={`rounded-xl border ${tone.border} ${tone.bg} p-4 flex items-start gap-3`}
    >
      <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${tone.iconColor}`} />
      <div className={`flex-1 text-sm ${tone.text}`}>
        <p className="font-bold mb-0.5">
          {isUnknown
            ? "Estado SRI no verificado"
            : `Contribuyente en estado: ${status}`}
        </p>
        <p className="text-xs opacity-90">
          {isUnknown
            ? "Este cliente no se ha sincronizado con el SRI. Te recomendamos revalidar antes de emitir la factura."
            : "El SRI rechaza facturas a contribuyentes que no están ACTIVO. Verifica con el cliente o revalida."}
        </p>
      </div>
      <button
        type="button" onClick={revalidate} disabled={revalidating}
        data-testid="sri-revalidate-button"
        className={`text-xs font-semibold inline-flex items-center gap-1 px-3 py-2 rounded-full border ${tone.border} ${tone.text} hover:bg-white/60`}
      >
        <RefreshCw className={`w-3.5 h-3.5 ${revalidating ? "animate-spin" : ""}`} />
        {revalidating ? "Verificando…" : "Revalidar SRI"}
      </button>
    </div>
  );
}
